/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable default-param-last */
import { Link, useLocation } from "react-router-dom";
import { FunctionComponent, useContext, useEffect, useState } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import styles from "./self-service.module.scss";
import { initialPaginationData } from "../utils/constants";
import Table, { Column } from "../components/table/Table";
import LayoutContext from "../context/layout-context";
import Modal from "../components/modal/Modal";
import { resources } from "../utils/resources";
import ContextMenu, {
  ContextOption
} from "../components/context-menu/ContextMenu";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import { ClassName, FileHistories } from "../utils/helpers/types";
import {
  dataForceRetry,
  dataResync,
  dataRetry,
  fetchClassNames,
  getAllFiles,
  getClassNames,
  getFileById
} from "../utils/helpers/data/file";
import Button from "../components/button/Button";
import Select, { Option } from "../components/select/Select";
import { downloadFile } from "../utils/helpers/data/task-history";

const statusMap: Record<string, string> = {
  COMPLETED: "success",
  FAILED: "error",
  ERROR: "error"
};
const statusOptions: Option[] = [
  {
    label: "COMPLETED",
    value: "COMPLETED"
  },
  {
    label: "FAILED",
    value: "FAILED"
  },
  {
    label: "PENDING",
    value: "PENDING"
  },
  {
    label: "STARTED",
    value: "STARTED"
  },

  {
    label: "ERROR",
    value: "ERROR"
  },
  {
    label: "INTERRUPTED",
    value: "INTERRUPTED"
  }
];

const dateFilterOptions: Option[] = [
  {
    label: "File Reference Date",
    value: "referenceDate"
  },

  {
    label: "Created Date",
    value: "dateTime"
  }
];

const Files: FunctionComponent = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const taskName = queryParams.get("taskName") || undefined;

  let timerRef: NodeJS.Timeout;
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [loading, setLoading] = useState(false);
  const { notify } = useContext(LayoutContext);
  const [file, setFile] = useState<FileHistories[]>([]);
  const [fileOnView, setFileOnView] = useState<FileHistories | null>(null);
  const [reset, setReset] = useState(false);
  const [classNamesOptions, setClassNamesOptions] = useState<Option[]>([]);
  const [processorOptions, setProcessorOptions] = useState<Option[]>([]);
  const [moduleName, setModuleName] = useState<string | undefined>(undefined);
  const [syncClassName, setSyncClassName] = useState<string | undefined>(
    undefined
  );
  const [syncStatus, setSyncStatus] = useState<string | undefined>(undefined);

  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const [dateFilterColumn, setDateFilterColumn] = useState<string | undefined>(
    undefined
  );
  const [downloadingProgress, setDownloadingProgress] = useState<
    Record<number, number>
  >({});

  const pageTitle = taskName ? `${taskName} File History` : "All Files History";

  const formatDateForApi = (date: Dayjs | null) => {
    return date ? date.format("YYYY-MM-DD") : "";
  };

  const formattedStartTime = formatDateForApi(startDate);
  const formattedEndTime = formatDateForApi(endDate);

  const fetchFiles = async (
    pageNumber: number = pagination.pageNumber,
    pageSize = pagination.pageSize,
    taskName?: string,
    syncClassName?: string,
    syncStatus?: string,
    moduleName?: string,
    formattedStartTime?: string,
    formattedEndTime?: string,
    dateFilterColumn?: string
  ) => {
    setReset(false);
    setLoading(true);

    if (startDate && endDate && !dateFilterColumn) {
      notify("error", "Please select a filter option for the date range.");
      setLoading(false);
      return;
    }

    if (!startDate && !endDate && dateFilterColumn) {
      notify("error", "Please select a date range for the filter option.");
      setLoading(false);
      return;
    }

    if (startDate && !endDate) {
      notify("error", "Kindly select the two dates.");
      setLoading(false);
      return;
    }

    const { data, error, message } = await getAllFiles(
      pageNumber,
      pageSize,
      taskName,
      syncClassName,
      syncStatus,
      moduleName,
      formattedStartTime,
      formattedEndTime,
      dateFilterColumn
    );
    setLoading(false);
    if (error || !data) {
      notify("error", `Unable to fetch file: ${message}`);
      return;
    }

    setFile(data.records);
    setPagination({
      ...pagination,
      pageNumber,
      total: data.total
    });

    setLoading(false);
  };

  const handleDownloadClick = async (
    id: number | undefined,
    taskName: string | undefined
  ) => {
    if (id === undefined) {
      notify("error", "Invalid history ID for download");
      return;
    }

    setDownloadingProgress(prev => ({ ...prev, [id]: 10 }));
    setLoading(true);

    try {
      const response = await downloadFile(id, taskName);

      if (response.error) {
        notify("error", response.message || "File download failed.");
      } else {
        notify(
          "success",
          response.message || `Download complete for ID: ${id}.`
        );
      }

      setDownloadingProgress(prev => ({ ...prev, [id]: 100 }));
    } catch (error) {
      notify("error", `An unexpected error occurred during download: ${error}`);
    } finally {
      setLoading(false);

      setTimeout(() => {
        setDownloadingProgress(prev => {
          const newState = { ...prev };
          delete newState[id];
          return newState;
        });
      }, 500);
    }
  };

  const columns: Column<FileHistories>[] = [
    {
      title: "Task Name",
      dataIndex: "taskName",
      key: "taskName",
      render: (taskName: string | undefined) => taskName || "N/A"
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string | undefined) => {
        if (!name) return "N/A";
        return name.length > 40 ? `${name.slice(0, 40)}...` : name;
      }
    },
    {
      title: "Task Processor",
      dataIndex: "moduleName",
      key: "moduleName",
      render: (moduleName: string | undefined) => {
        if (!moduleName) return "N/A";

        const parts = moduleName.split("serviceImpl.");
        return parts.length > 1 ? parts[1] : moduleName;
      }
    },
    {
      title: "Sync Status",
      dataIndex: "syncStatus",
      key: "syncStatus",
      render: (value: string | undefined) => (
        <span className={`tag ${statusMap[value || ""] || ""}`}>
          {value || "N/A"}
        </span>
      )
    },
    {
      title: "Sync Medium",
      dataIndex: "syncMedium",
      key: "syncMedium",
      render: (syncMedium: string | undefined) => syncMedium || "N/A"
    },
    {
      title: "Reference Date",
      dataIndex: "referenceDate",
      key: "referenceDate",
      render: (referenceDate: string) =>
        referenceDate
          ? dayjs(referenceDate).format("YYYY-MM-DD HH:mm:ss")
          : "N/A"
    },
    {
      title: "Created Date",
      dataIndex: "dateTime",
      key: "dateTime",
      render: (dateTime: string | undefined) =>
        dateTime ? dayjs(dateTime).format("YYYY-MM-DD HH:mm:ss") : "N/A"
    },

    {
      title: "",
      render: (_: unknown, file: FileHistories) => {
        const progress = downloadingProgress[file.id || -1];
        const isDownloading = progress !== undefined;
        const options: ContextOption[] = [
          {
            label: "View Details",
            onClick: () => {
              setFileOnView(file);
            }
          },
          {
            label: (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%"
                }}
              >
                <span>Download</span>
                {isDownloading && (
                  <div
                    style={{
                      height: "6px",
                      width: "40%",
                      backgroundColor: "#e0e0e0",
                      borderRadius: "3px",
                      marginLeft: "10px",
                      overflow: "hidden"
                    }}
                    title={`Downloading: ${progress}%`}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${progress}%`,
                        backgroundColor: "#007bff",
                        transition: "width 0.1s linear"
                      }}
                    />
                  </div>
                )}
              </div>
            ),
            onClick: () => {
              if (!isDownloading) {
                handleDownloadClick(file.id, file.taskName);
              }

              return false;
            },
            disabled: isDownloading
          }
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      }
    }
  ];

  useEffect(() => {
    clearTimeout(timerRef);
    timerRef = setTimeout(() => {
      fetchFiles(
        pagination.pageNumber,
        pagination.pageSize,
        taskName,
        syncClassName,
        syncStatus,
        moduleName,
        formattedStartTime,
        formattedEndTime,
        dateFilterColumn
      );
    }, 400);
  }, [
    pagination.pageSize,
    taskName,
    syncClassName,
    syncStatus,
    moduleName,
    formattedStartTime,
    formattedEndTime,
    dateFilterColumn
  ]);

  const resetFilters = () => {
    setSyncStatus(undefined);
    setSyncClassName(undefined);
    setModuleName(undefined);
    setStartDate(null);
    setEndDate(null);
    setDateFilterColumn(undefined);
    setPagination({ ...pagination, pageNumber: 1 });
  };

  useEffect(() => {
    const fetchClassNamesFromBackend = async () => {
      try {
        const response = await getClassNames();
        if (!response.error) {
          const classNamesData: ClassName[] = response.data || [];
          const options = classNamesData.map(className => ({
            label: className.className,
            value: className.fullClass
          }));
          setClassNamesOptions(options);
        }
      } catch (error) {
        console.error("Unable to fetch class names: ", error);
      }
    };
    fetchClassNamesFromBackend();
  }, []);

  useEffect(() => {
    const fetchProcessorFromBackend = async () => {
      try {
        const response = await fetchClassNames();
        if (!response.error) {
          const classNamesData: ClassName[] = response.data || [];
          const options = classNamesData.map(className => ({
            label: className.className,
            value: className.fullClass
          }));
          setProcessorOptions(options);
        }
      } catch (error) {
        console.error("Unable to fetch processor: ", error);
      }
    };
    fetchProcessorFromBackend();
  }, []);

  return (
    <main>
      <h1 className="title vertical-margin spaced thin">{pageTitle}</h1>

      <div className="vertical-margin spaced">
        <div style={{ display: "flex", gap: "10px", width: "60%" }}>
          <div className="input-group">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div style={{ display: "flex", gap: "10px" }}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={newValue => setStartDate(newValue)}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={newValue => setEndDate(newValue)}
                />
              </div>
            </LocalizationProvider>
          </div>

          <div className="input-group">
            <Select
              options={dateFilterOptions}
              value={dateFilterColumn || ""}
              onSelect={value => setDateFilterColumn(value as string)}
              placeholder="Select Date Filter"
              responsive
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", width: "60%" }}>
          <div className="input-group ">
            <Select
              options={classNamesOptions}
              value={syncClassName || ""}
              onSelect={value => setSyncClassName(value as string)}
              placeholder="Select Sync Class"
              responsive
            />
          </div>

          <div className="input-group ">
            <Select
              options={statusOptions}
              value={syncStatus || ""}
              onSelect={value => setSyncStatus(value as string)}
              placeholder="Select Sync State"
              responsive
            />
          </div>

          <div className="input-group ">
            <Select
              options={processorOptions}
              value={moduleName || ""}
              onSelect={value => setModuleName(value as string)}
              placeholder="Select Processor"
              responsive
            />
          </div>
        </div>

        {(syncClassName ||
          syncStatus ||
          moduleName ||
          startDate ||
          endDate) && (
          <Button type="accent" size="small" onClick={resetFilters}>
            RESET FILTERS
          </Button>
        )}
      </div>

      <Table
        dataSource={file || []}
        columns={columns}
        pagination={pagination}
        onPageChange={fetchFiles}
        onPageSizeChange={pageSize => {
          console.log("PAGE-SIZE -- ", pageSize);
          setPagination({ ...pagination, pageSize, pageNumber: 1 });
        }}
        loading={loading}
        className="smaller"
      />

      <TaskViewModal
        file={fileOnView}
        cancel={() => setFileOnView(null)}
        refresh={() => fetchFiles()}
        visible={Boolean(fileOnView)}
        fetchFiles={fetchFiles}
      />

      <br />
      <br />

      <Button>
        <Link to="/task-creation">Back to Task</Link>
      </Button>
    </main>
  );
};

interface TaskEditModalProps {
  file: FileHistories | null;
  cancel: () => void;
  refresh: (pageNumber?: number) => void;
  visible: boolean;
  fetchFiles: (pageNumber?: number) => void;
}

const TaskViewModal: FunctionComponent<TaskEditModalProps> = ({
  file: _history,
  cancel,
  visible,
  refresh,
  fetchFiles
}) => {
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<FileHistories | null>(_history);

  const { notify } = useContext(LayoutContext);

  useEffect(() => {
    setFile(_history);
  }, [_history]);

  // const handleRefresh = async (id: number) => {
  //   setLoading(true);
  //   try {
  //     const response = await dataResync(id);
  //     if (!response.error) {
  //       const { data, message } = response;
  //       notify("success", `${message}: ${data}`);
  //     } else {
  //       notify("error", `Data synchronization failed: ${response.message}`);
  //     }
  //   } catch (error: any) {
  //     notify("error", `Data synchronization failed: ${error.message}`);
  //     console.error("Error during sync:", error);
  //   } finally {
  //     setLoading(false);
  //   }
  //   fetchFiles();
  // };

  const handleRefresh = async (id: number) => {
    setLoading(true);
    try {
      const response = await dataResync(id);
      if (!response.error) {
        const { data, message } = response;
        notify("success", `${message}: ${data}`);

        const refreshedFile = await getFileById(id);
        if (!refreshedFile.error && refreshedFile.data) {
          setFile(refreshedFile.data);
        }
      } else {
        notify("error", `Data synchronization failed: ${response.message}`);
      }
    } catch (error: any) {
      notify("error", `Data synchronization failed: ${error.message}`);
      console.error("Error during sync:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReTry = async (id: number) => {
    setLoading(true);
    try {
      const response = await dataRetry(id);
      if (!response.error) {
        const { data, message } = response;
        notify("success", `${message}: ${data}`);
      } else {
        notify("error", `Retry failed: ${response.message}`);
      }
    } catch (error: any) {
      notify("error", `Data retry failed: ${error.message}`);
      console.error("Error during retry:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleForceRetry = async (id: number) => {
    setLoading(true);
    try {
      const response = await dataForceRetry(id, true);
      if (!response.error) {
        const { data, message } = response;
        notify("success", `${message}: ${data}`);
      } else {
        notify("error", `Force retry failed: ${response.message}`);
      }
    } catch (error: any) {
      notify("error", `Force retry failed: ${error.message}`);
      console.error("Error during force retry:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal visible={visible} cancel={cancel} ignoreBackdropClicks>
      {loading ? (
        <img
          alt="loading"
          src={resources.images.LOADING_BLUE}
          className="generic-icon xxl"
        />
      ) : (
        <section className={styles.details}>
          <br />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <h4 style={{ color: "green" }}>RECORD DETAILS</h4>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Button
                style={{ marginBottom: "0.2rem" }}
                onClick={() => {
                  if (file?.id) {
                    handleReTry(file.id);
                  }
                }}
                disabled={!file?.syncClassName}
              >
                Retry
              </Button>

              <span style={{ marginLeft: "1rem" }} />

              <Button
                style={{ marginBottom: "0.2rem" }}
                onClick={() => {
                  if (file?.id) {
                    handleForceRetry(file.id);
                  }
                }}
                disabled={!file?.syncClassName}
              >
                Force Retry
              </Button>
            </div>
          </div>

          <hr />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ width: "40%" }}>
              <div className={styles.detail}>
                <span className={styles.key}>Task Name</span>
                <span className={styles.value}>{file?.taskName || "N/A"}</span>
              </div>

              <div className={styles.detail}>
                <span className={styles.key}>Sync Status:</span>
                <span className={styles.value}>
                  {file?.syncStatus || "N/A"}
                </span>
              </div>

              <div className={styles.detail}>
                <span className={styles.key}>Synced At:</span>
                <span className={styles.value}>
                  {file?.syncedAt
                    ? dayjs(file.syncedAt).format("YYYY-MM-DD HH:mm:ss")
                    : "N/A"}
                </span>
              </div>

              <div className={styles.detail}>
                <span className={styles.key}>Sync Medium:</span>
                <span className={styles.value}>
                  {file?.syncMedium || "N/A"}
                </span>
              </div>

              <div className={styles.detail}>
                <span className={styles.key}>Reference Date:</span>
                <span className={styles.value}>
                  {file?.referenceDate
                    ? dayjs(file.referenceDate).format("YYYY-MM-DD HH:mm:ss")
                    : "N/A"}
                </span>
              </div>

              <div className={styles.detail}>
                <span className={styles.key}>Date Time:</span>
                <span className={styles.value}>
                  {file?.dateTime
                    ? dayjs(file.dateTime).format("YYYY-MM-DD HH:mm:ss")
                    : "N/A"}
                </span>
              </div>

              <div className={styles.detail}>
                <span className={styles.key}>File Sync Processor:</span>
                <span className={styles.value}>
                  {file?.fileSyncProcessor || "N/A"}
                </span>
              </div>

              <div className={styles.detail}>
                <span className={styles.key}>Module Name:</span>
                <span className={styles.value}>
                  {file?.moduleName
                    ? file.moduleName.includes("serviceImpl.")
                      ? file.moduleName.split("serviceImpl.")[1]
                      : file.moduleName
                    : "N/A"}
                </span>
              </div>
            </div>

            <div style={{ width: "60%" }}>
              <div className={styles.detail}>
                <span className={styles.key}>Hash:</span>
                <span className={styles.value}>{file?.hash || "N/A"}</span>
              </div>
              <div className={styles.detail}>
                <span className={styles.key}>Path:</span>
                <span className={styles.value}>{file?.path || "N/A"}</span>
              </div>
              <div className={styles.detail}>
                <span className={styles.key}>Remote Path:</span>
                <span className={styles.value}>
                  {file?.remotePath || "N/A"}
                </span>
              </div>
              <div className={styles.detail}>
                <span className={styles.key}>Size:</span>
                <span className={styles.value}>{file?.size || "N/A"}</span>
              </div>
              <div className={styles.detail}>
                <span className={styles.key}>Type:</span>
                <span className={styles.value}>{file?.type || "N/A"}</span>
              </div>
            </div>
          </div>

          <br />
          <br />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <h4 style={{ color: "green" }}>SYNCHRONIZATION DATA</h4>
            <div style={{ display: "flex", alignItems: "center" }}>
              <h4
                style={{ color: "green", cursor: "pointer" }}
                onClick={() => {
                  if (file?.id) {
                    handleRefresh(file.id);
                  }
                }}
              >
                Refresh
              </h4>
              <i
                className="fas fa-sync-alt"
                style={{ marginLeft: "8px", cursor: "pointer", color: "green" }}
                onClick={() => {
                  if (file?.id) {
                    handleRefresh(file.id);
                  }
                }}
              />
            </div>
          </div>

          <hr />
          <div
            className={styles.detail}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start"
            }}
          >
            {file?.syncConnectionData
              ? Object.entries(JSON.parse(file.syncConnectionData)).map(
                  ([key, value]) => (
                    <div key={key} className={styles.detail}>
                      <span className={styles.key}>
                        {key.charAt(0).toUpperCase() + key.slice(1)}:
                      </span>
                      <span className={styles.value}>{String(value)}</span>
                    </div>
                  )
                )
              : "N/A"}
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Retry Count:</span>
            <span className={styles.value}>
              {file?.syncRetryCount || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>File sync handler:</span>
            <span className={styles.value}>
              {file?.syncClassName?.split("ioImpl.")[1] || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Sync Summary:</span>
            <span className={styles.value}>{file?.syncSummary || "N/A"}</span>
          </div>
        </section>
      )}
    </Modal>
  );
};

export default Files;
